<template>
  <v-container>
    <v-row v-if="loadingRecords" justify="center" align="center">
      <v-progress-circular indeterminate :size="60" color="primary" />
    </v-row>

    <v-row v-else-if="APIError" justify="center" align="center">
      <p>
        <v-icon color="error">mdi-alert-circle-outline</v-icon> Sorry, something
        went wrong. Please try again later.
      </p>
    </v-row>

    <v-container v-else>
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title>
              <p class="headline">Lifting Progress</p>
            </v-card-title>
            <apexchart height="400" :options="chartOptions" :series="getChartData()"></apexchart>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title>
              <p class="headline">Completed Exercises</p>
              <v-spacer></v-spacer>
              <v-text-field v-model="tableSearchQuery" append-icon="mdi-magnify" label="Search" single-line
                hide-details></v-text-field>
            </v-card-title>
            <v-data-table :headers="tableHeaders" :items="exerciseData" :search="tableSearchQuery">
              <template v-slot:item.actions="{ item }">
                <v-icon small @click="deleteExercise(item)">
                  mdi-delete
                </v-icon>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog v-model="dialog" max-width="400">
      <v-card>
        <v-card-title class="headline">Delete Exercise Record</v-card-title>
        <v-card-text>
          Are you sure you want to delete this exercise record?
          <p class="mt-4" v-if="selectedExercise">
            {{ selectedExercise.exerciseName }} - {{ selectedExercise.date }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="dialog = false">Cancel</v-btn>
          <v-btn color="red darken-1" text @click="confirmDelete" :loading="loading">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from "axios";
import VueApexCharts from 'vue-apexcharts'
import { mapState } from 'vuex'

export default {
  name: "TrainingHistory",

  components: {
    apexchart: VueApexCharts,
  },

  data: () => ({
    APIError: false,
    loadingRecords: false,
    tableSearchQuery: "",
    tableHeaders: [
      {
        text: 'Date', value: 'date',
      },
      { text: 'Name', value: 'exerciseName' },
      { text: 'Weight', value: 'weight' },
      { text: '1 set', value: 'firstSetReps' },
      { text: '2 set', value: 'secondSetReps' },
      { text: '3 set', value: 'thirdSetReps' },
      { text: 'Actions', value: 'actions' },
    ],
    chartOptions: {
      chart: {
        fontFamily: 'Roboto, sans-serif',
        animations: {
          enabled: false,
        },
        toolbar: {
          show: false
        }
      },
      markers: {
        size: 5,
      },
    },
    dialog: false,
    loading: false,
    selectedExercise: null,
  }),

  mounted() {
    if (this.completedExercises.length == 0 || this.exercises.length == 0) {
      this.getData()
    }
  },

  computed: {
    ...mapState([
      'completedExercises',
      'exercises',
    ]),
    exerciseData: function () {
      return this.completedExercises.map(completedExercise => {
        const exercise = this.exercises.find(
          exercise => exercise.id === completedExercise.exercise
        )
        completedExercise.exerciseName = exercise.name
        return completedExercise
      });
    }
  },

  methods: {
    getCompletedExercises: function () {
      return axios.get(
        `${process.env.VUE_APP_API_ROOT_URL}training/completed-exercises/`,
        this.$store.getters.axiosConfig
      )
    },
    getExercises: function () {
      return axios.get(
        `${process.env.VUE_APP_API_ROOT_URL}training/exercises/`,
        this.$store.getters.axiosConfig
      )
    },
    getData: function () {
      this.loadingRecords = true
      this.APIError = false
      Promise.all([this.getExercises(), this.getCompletedExercises()])
        .then(([exercises, completedExercises]) => {
          this.$store.commit("updateExercises", exercises.data)
          this.$store.commit("updateCompletedExercises", completedExercises.data)
          this.loadingRecords = false
        })
        .catch(() => {
          this.APIError = true
          this.loadingRecords = false
        })
    },
    getExerciseName: function (id) {
      return this.exercises[id]
    },
    getSeries: function (exerciseName, exerciseData) {
      const series = {
        type: "line",
        name: exerciseName,
        data: exerciseData
      }
      return series
    },
    getChartData: function () {
      let chartData = []
      this.exercises.forEach(exercise => {
        const maxRepExercises = this.completedExercises.filter(completedExercise => {
          return ((completedExercise.exercise === exercise.id) &&
            (completedExercise.maxRepsCompleted === true))
        })
        const exerciseData = maxRepExercises.reverse().map(exercise => (
          { x: exercise.date, y: exercise.weight }
        ))
        if (exerciseData.length > 0) {
          chartData.push(this.getSeries(exercise.name, exerciseData))
        }
      })
      return chartData
    },
    deleteExercise(exercise) {
      this.selectedExercise = exercise;
      this.dialog = true;
    },
    confirmDelete() {
      this.loading = true;
      axios
        .delete(
          `${process.env.VUE_APP_API_ROOT_URL}training/completed-exercises/${this.selectedExercise.id}/`,
          this.$store.getters.axiosConfig
        )
        .then(() => {
          this.getData();
          this.dialog = false;
        })
        .catch(() => {
          this.APIError = true;
        })
        .finally(() => {
          this.loading = false;
          this.selectedExercise = null;
        });
    },
  }
}
</script>